import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useRoles } from "@/context/RolesContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";
import { APP_NAME, APP_TAGLINE, absUrl } from "@/lib/config";
import {
  House, Clock, CalendarBlank, Kanban, UsersThree, User, SignOut, Buildings,
  Package, Megaphone, ChartBar, List, X, Bell, At, ShieldCheck, ClockCounterClockwise,
  CaretDown, Wallet, Rocket, DoorOpen, Trophy, ChartLine, ListChecks,
  UserPlus, Briefcase, WarningCircle, Eye, ClipboardText, CalendarCheck,
} from "@phosphor-icons/react";
import OnboardingGate from "@/components/OnboardingGate";
import IdleTracker from "@/components/IdleTracker";

// Built-in role fixed colors. Custom roles use their `color` field via useRoles().
const BUILT_IN_ROLE_MAP = {
  admin: { bg: "bg-[#002FA7] text-white", label: "ADMIN" },
  manager: { bg: "bg-[#FF3B30] text-white", label: "MANAGER" },
  hr: { bg: "bg-[#7C3AED] text-white", label: "HR" },
  employee: { bg: "bg-[#0A0A0A] text-white", label: "EMPLOYEE" },
};
const roleBadge = (roleKey, roleDoc) => {
  if (BUILT_IN_ROLE_MAP[roleKey]) return BUILT_IN_ROLE_MAP[roleKey];
  return {
    bg: "text-white",
    style: { backgroundColor: roleDoc?.color || "#0A0A0A" },
    label: (roleDoc?.label || roleKey).toUpperCase(),
  };
};

/**
 * NotificationsBell — TOP-BAR variant.
 * Dropdown opens DOWN and to the LEFT of the bell (right-anchored) so it stays
 * inside the viewport when placed in the top-right header.
 */
function NotificationsBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const seenIds = useRef(new Set());
  const dropdownRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setCount(r.data.count || 0);
    } catch {}
  };
  const loadList = async () => {
    try {
      const r = await api.get("/notifications");
      const list = r.data || [];
      setItems(list);
      // Check for new achievement notifications (not yet seen this session)
      const newAch = list.find(n => n.type === "achievement_earned" && !n.read && !seenIds.current.has(n.id));
      if (newAch) {
        newAch && seenIds.current.add(newAch.id);
        setToast(newAch);
        // Fire confetti 🎉
        try {
          confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
          setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } }), 250);
          setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } }), 400);
        } catch {}
        setTimeout(() => setToast(null), 6000);
      }
      // Mark all currently-seen ach IDs
      list.filter(n => n.type === "achievement_earned").forEach(n => seenIds.current.add(n.id));
    } catch {}
  };

  useEffect(() => {
    refresh();
    loadList();
    const t = setInterval(() => { refresh(); loadList(); }, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { if (open) loadList(); }, [open]);

  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const click = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    setOpen(false);
    refresh();
    if (n.type === "achievement_earned") {
      navigate("/gamification");
    } else if (n.project_id) {
      navigate(`/projects/${n.project_id}?task=${n.task_id}`);
    } else if (n.link) {
      navigate(n.link);
    }
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    refresh();
    loadList();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="p-2 hover:bg-muted relative flex items-center justify-center"
        data-testid="notifications-bell"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={18} weight="bold" />
        {count > 0 && (
          <span
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-badge"
          >{count > 99 ? "99+" : count}</span>
        )}
      </button>
      {toast && (
        <div className="fixed top-16 right-4 z-[60] bg-gradient-to-r from-[#FFD700] to-[#F59E0B] text-black border-2 border-black shadow-2xl p-4 min-w-[280px] max-w-sm" data-testid="achievement-toast">
          <div className="flex items-start gap-3">
            <div className="text-4xl">{toast.achievement_icon || "🏆"}</div>
            <div className="flex-1">
              <div className="eyebrow mb-1">🎉 Achievement unlocked!</div>
              <div className="font-display font-black text-lg leading-tight">{toast.achievement_label}</div>
              <button onClick={() => { setToast(null); navigate("/gamification"); }}
                className="mt-2 text-xs underline hover:text-[#0A0A0A]">View trophy cabinet →</button>
            </div>
            <button onClick={() => setToast(null)} className="text-lg leading-none">×</button>
          </div>
        </div>
      )}
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-80 bg-white border border-border shadow-lg z-50"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="eyebrow">Notifications</div>
            {items.some(i => !i.read) && (
              <button onClick={markAllRead} className="text-[10px] font-mono-mini uppercase hover:text-[#002FA7]" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">No notifications yet.</div>
            ) : items.map(n => (
              <button
                key={n.id}
                onClick={() => click(n)}
                className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted transition-colors ${!n.read ? "bg-blue-50/30" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {n.type === "achievement_earned" ? (
                    <span className="text-lg mt-0.5 shrink-0">{n.achievement_icon || "🏆"}</span>
                  ) : (
                    <At size={14} weight="bold" className="mt-0.5 text-[#7C3AED] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs">
                      {n.type === "achievement_earned" ? (
                        <>
                          <span className="font-semibold">Achievement unlocked:</span>{" "}
                          <span className="text-[#F59E0B] font-semibold">{n.achievement_label}</span>
                        </>
                      ) : n.actor_name ? (
                        <>
                          <span className="font-semibold">{n.actor_name}</span>
                          <span className="text-muted-foreground"> mentioned you on </span>
                          <span className="font-semibold">{n.task_title}</span>
                        </>
                      ) : (
                        <span>{n.text || n.type}</span>
                      )}
                    </div>
                    {n.preview && <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{n.preview}</div>}
                    <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">
                      {n.created_at?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-[#002FA7] shrink-0 mt-1.5" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * UserMenu — the account button that lives in the top-right corner.
 * Shows avatar + name + role chip. Clicking opens a dropdown with a link to
 * /profile and a Sign out action.
 */
function UserMenu() {
  const { user, logout } = useAuth();
  const { byKey: roleByKey } = useRoles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const userRoles = user.roles && user.roles.length ? user.roles : [user.role];
  const primaryRoleKey = userRoles[0];
  const primaryBadge = roleBadge(primaryRoleKey, roleByKey(primaryRoleKey));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 py-1 pr-2 pl-1 hover:bg-muted"
        data-testid="user-menu-button"
      >
        {user.avatar ? (
          <img src={absUrl(user.avatar)} alt={user.name} className="w-8 h-8 object-cover" data-testid="user-avatar" />
        ) : (
          <div className="w-8 h-8 bg-[#0A0A0A] text-white flex items-center justify-center font-display font-bold text-sm" data-testid="user-avatar">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
        )}
        <div className="hidden sm:block text-left min-w-0">
          <div className="text-xs font-semibold truncate max-w-[140px]" data-testid="user-name">{user.name}</div>
          <div className="flex flex-wrap gap-0.5 mt-0.5">
            <span
              className={`text-[9px] px-1 py-[1px] ${primaryBadge.bg}`}
              style={primaryBadge.style || {}}
              data-testid={`user-role-${primaryRoleKey}`}
            >
              {primaryBadge.label}
            </span>
            {userRoles.length > 1 && (
              <span className="text-[9px] px-1 py-[1px] bg-muted text-muted-foreground font-mono-mini" title={userRoles.slice(1).join(", ")}>
                +{userRoles.length - 1}
              </span>
            )}
          </div>
        </div>
        <CaretDown size={12} weight="bold" className="hidden sm:block text-muted-foreground" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-border shadow-lg z-50"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-border">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {userRoles.map(r => {
                const b = roleBadge(r, roleByKey(r));
                return (
                  <span
                    key={r}
                    className={`text-[10px] px-1.5 py-0.5 ${b.bg}`}
                    style={b.style || {}}
                  >
                    {b.label}
                  </span>
                );
              })}
            </div>
          </div>
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-3 hover:bg-muted text-sm border-b border-border"
            data-testid="user-menu-profile"
          >
            <User size={14} weight="bold" /> Profile
          </Link>
          <button
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#0A0A0A] hover:text-white text-sm text-left"
            data-testid="user-menu-logout"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { branding } = useBranding();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "1");
  const [hasReview, setHasReview] = useState(false);
  const toggle = () => {
    setCollapsed(c => { const n = !c; localStorage.setItem("sidebar-collapsed", n ? "1" : "0"); return n; });
  };

  // Performance Reviews nav visibility: show it whenever the user actually
  // has at least one review (as subject/reviewer/initiator), independent of
  // whether they can initiate. This makes the module "appear" for an
  // employee only after their manager starts a review for them — matching
  // the product requirement.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/performance-reviews", { params: { scope: "mine" } });
        setHasReview((r.data || []).length > 0);
      } catch { /* ignore */ }
    })();
  }, [user?.id]);

  if (!user) return null;

  // Base nav visible to everyone. Admin-restricted items gate on capabilities so
  // any custom role granted the right capability sees them.
  // NOTE: Notifications and Profile intentionally moved to the top-right header.
  const showPerf = hasReview || can("initiate_performance_review") ||
                   can("view_all_performance_reviews") || can("manage_performance_reviews");
  
  // Navigation menu - organized in logical groups
  const nav = [
    // === CORE NAVIGATION ===
    { to: "/dashboard", label: "Dashboard", icon: House, show: true },
    
    // === PROJECT MANAGEMENT ===
    { to: "/projects", label: "Projects", icon: Kanban, show: true },
    { to: "/tasks", label: "All Tasks", icon: ListChecks, show: true },
    { to: "/issues", label: "Issues", icon: WarningCircle, show: can("use_issues") },
    
    // === TIME & ATTENDANCE ===
    { to: "/attendance", label: "Attendance", icon: Clock, show: true },
    { to: "/timesheet", label: "Timesheet", icon: ClockCounterClockwise, show: can("use_timesheet") || can("approve_timesheets") },
    { to: "/leaves", label: "Leave", icon: CalendarBlank, show: true },
    { to: "/leave-calendar", label: "Leave Calendar", icon: CalendarCheck, show: true },
    
    // === HR & PEOPLE ===
    { to: "/directory", label: "Directory", icon: UsersThree, show: can("create_users") || can("disable_users") || can("delete_users") || can("view_reports") || can("view_employee_documents") },
    { to: "/performance", label: "Performance", icon: ClipboardText, show: showPerf },
    { to: "/onboarding", label: "Onboarding", icon: Rocket, show: can("use_onboarding") },
    { to: "/offboarding", label: "Offboarding", icon: DoorOpen, show: can("use_offboarding") },
    
    // === FINANCE & EXPENSES ===
    { to: "/expenses", label: "Expenses", icon: Wallet, show: can("use_expenses") || can("approve_expenses") || can("view_all_expenses") },
    
    // === RECRUITMENT ===
    { to: "/jobs", label: "Job Openings", icon: Briefcase, show: can("use_jobs") },
    { to: "/referrals", label: "Referrals", icon: UserPlus, show: can("use_referrals") },
    
    // === RESOURCES & TOOLS ===
    { to: "/assets", label: "Assets", icon: Package, show: true },
    { to: "/announcements", label: "Announcements", icon: Megaphone, show: true },
    
    // === INSIGHTS & MONITORING ===
    { to: "/insights", label: "Insights", icon: ChartLine, show: true },
    { to: "/reports", label: "Reports", icon: ChartBar, show: can("view_reports") },
    { to: "/idle-tracker", label: "Activity Monitor", icon: Eye, show: can("view_idle_tracker") },
    { to: "/gamification", label: "Achievements", icon: Trophy, show: can("use_gamification") },
    
    // === ADMINISTRATION ===
    { to: "/settings", label: "Settings", icon: ShieldCheck, show: can("manage_permissions") || can("manage_roles") || can("manage_timesheet_config") || can("manage_branding") || can("manage_email") || can("manage_expenses_config") || can("manage_onboarding_config") || can("create_leave_templates") || can("manage_referrals_config") || can("manage_job_templates") || can("manage_issue_templates") || can("manage_backups") || can("manage_idle_tracker_config") || can("manage_holidays") },
  ];

  return (
    <div className="min-h-screen flex bg-white">
      <aside className={`${collapsed ? "w-16" : "w-64"} shrink-0 border-r border-border flex flex-col bg-white transition-all duration-200`} data-testid="app-sidebar">
        <div className={`${collapsed ? "px-3" : "px-6"} py-6 border-b border-border flex items-center justify-between gap-2`}>
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
            {branding.logo_url ? (
              <img src={absUrl(branding.logo_url)} alt={branding.brand_name || APP_NAME} className="w-8 h-8 object-cover shrink-0" />
            ) : (
              <div
                className="w-8 h-8 flex items-center justify-center shrink-0"
                style={{ backgroundColor: branding.brand_mark_color || "#002FA7" }}
              >
                <Buildings size={18} weight="bold" color="white" />
              </div>
            )}
            {!collapsed && (
              <div>
                <div className="font-display text-lg font-black leading-none">{branding.brand_name || APP_NAME}</div>
                <div className="eyebrow text-[10px] mt-1">{branding.brand_tagline || APP_TAGLINE}</div>
              </div>
            )}
          </Link>
          <button onClick={toggle} className="p-1 hover:bg-muted" data-testid="sidebar-toggle" title={collapsed ? "Expand" : "Collapse"}>
            {collapsed ? <List size={16} weight="bold" /> : <X size={16} weight="bold" />}
          </button>
        </div>

        <nav className="flex-1 py-4">
          {nav.filter(n => n.show).map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={18} weight="bold" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-x-hidden flex flex-col min-w-0">
        {/* Top header — houses Notifications + user menu on the right */}
        <header
          className="h-14 border-b border-border bg-white flex items-center justify-end gap-1 px-4 sticky top-0 z-30"
          data-testid="app-topbar"
        >
          <NotificationsBell />
          <div className="w-px h-6 bg-border mx-1" />
          <UserMenu />
        </header>

        <div className="flex-1 min-h-0 flex flex-col">
          <OnboardingGate />
          <Outlet />
        </div>
      </main>
      {/* Idle screen tracker — hidden daemon + persistent banner */}
      <IdleTracker />
    </div>
  );
}
